import { Button, Link, Text } from "@zapier/design-system";
import { useAtomValue, useSetAtom, useAtom } from "jotai";
import {
  updateNodeSetter,
  newAssetNameAtom,
  chatbotsAtom_SUSPENDS,
  chatbotUsageAtom_SUSPENDS,
  getUpgradePlanType,
  emitEventOnChatbotsPlanUpgradeSetter,
  ChatbotsPlanName,
  saveCanvasSetter,
} from "@zapier/canvas-state";
import { useCallback, useState } from "react";
import { CREATE_NEW_CHATBOTS_BUTTON_TEXT } from "@zapier/canvas-constants";

import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
import useIsOwnerAdminOrSuperAdmin from "../../hooks/useIsOwnerAdminOrSuperAdmin";
import { SpinnerButton } from "../SpinnerButton";

export function CreateNewChatbotButton({ nodeId }: { nodeId: string }) {
  const [createNewChatbotLoading, setCreateNewChatbotLoading] = useState(false);
  const name = useAtomValue(newAssetNameAtom);
  const refreshChatbots = useSetAtom(chatbotsAtom_SUSPENDS);
  const updateNode = useSetAtom(updateNodeSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const { emitEventOnChatbotCreation } = useEmitResourceCreatedEvents();
  const [error, setError] = useState<Error | null>(null);
  // This is after creating a new chatbot fails
  const [needsToUpgrade, setNeedsToUpgrade] = useState(false);
  // This is data we fetch before trying to create new chatbot
  const [chatbotsUsage, refreshChatbotsUsage] = useAtom(
    chatbotUsageAtom_SUSPENDS,
  );
  const isUpgradeNeeded = needsToUpgrade || chatbotsUsage?.needsToUpgrade;

  const createNewChatbot = useCallback(async () => {
    async function handleCreateNewChatbot() {
      setCreateNewChatbotLoading(true);
      try {
        setNeedsToUpgrade(false);
        const response = await createNewChatbotAsync(name);

        if ("errors" in response) {
          throw response;
        }

        const chatbotId = "data" in response ? response.data.id : null;

        if (chatbotId) {
          const updatedNodes = updateNode({
            id: nodeId,
            data: {
              chatbotId,
            },
          });

          saveToBackend({ nodes: updatedNodes });
          // Trigger chatbots to be re-fetched, so the new chatbot can be populated in the Chatbot Typeahead
          refreshChatbots();
          refreshChatbotsUsage(); // Because chatbot count would've changed

          emitEventOnChatbotCreation(chatbotId);
        }

        setError(null);
      } catch (e: unknown) {
        const error = e as ErrorObject;

        if (error && error.code === "chatbots:limits:chatbots") {
          setNeedsToUpgrade(true);
        } else {
          setError(
            new Error(
              "We ran into an unexpected error trying to create a new chatbot. Try again.",
            ),
          );
        }
      } finally {
        setCreateNewChatbotLoading(false);
      }
    }

    void handleCreateNewChatbot();
  }, [
    name,
    updateNode,
    nodeId,
    saveToBackend,
    refreshChatbots,
    emitEventOnChatbotCreation,
    refreshChatbotsUsage,
  ]);

  if (error) {
    throw error;
  }

  if (createNewChatbotLoading) {
    return <SpinnerButton />;
  }

  return (
    <>
      <Button
        color="primary"
        iconBefore={createNewChatbotLoading ? undefined : "formAdd"}
        onClick={createNewChatbot}
        disabled={isUpgradeNeeded}
      >
        {CREATE_NEW_CHATBOTS_BUTTON_TEXT}
      </Button>
      {isUpgradeNeeded && (
        <UpsellMessage currentPlanName={chatbotsUsage?.plan.name ?? "free"} />
      )}
    </>
  );
}

function UpsellMessage({
  currentPlanName,
}: {
  currentPlanName: ChatbotsPlanName;
}) {
  const isAdmin = useIsOwnerAdminOrSuperAdmin();
  const emitEventOnChatbotsPlanUpgrade = useSetAtom(
    emitEventOnChatbotsPlanUpgradeSetter,
  );
  const planType = getUpgradePlanType({ type: "chatbots", currentPlanName });
  const href = `/app/planbuilder/add-ons?product=chatbots-${planType}`;

  if (isAdmin) {
    return (
      <Text tag="p" type="SmallPrint1" textAlign="left" color="GrayWarm10">
        You&apos;ve reached your plan&apos;s Chatbots limit.&nbsp;
        <Link
          href={href}
          target="_blank"
          onClick={() =>
            emitEventOnChatbotsPlanUpgrade("Upgrade to create more Chatbots.")
          }
        >
          Upgrade
        </Link>
        &nbsp;to create more Chatbots.
      </Text>
    );
  }

  return (
    <Text tag="p" type="SmallPrint1" textAlign="left" color="GrayWarm10">
      You&apos;ve reached your plan&apos;s Chatbots limit. Talk to your admin
      about upgrading to create more Chatbots.
    </Text>
  );
}

type NewChatbotSuccessResponse = {
  data: {
    id: string;
    name: string;
  };
  code: string;
  detail: string;
};

type ErrorObject = {
  detail: string;
  code: string;
  errors: {
    status: number;
    code: string;
    detail: string;
  }[];
};

type NewChatbotErrorResponse = {
  error: ErrorObject;
};

type NewChatbotResponse = NewChatbotSuccessResponse | NewChatbotErrorResponse;

async function createNewChatbotAsync(name: string) {
  const response = await fetch(`/api/canvasui/chatbots`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return response.json() as Promise<NewChatbotResponse>;
}
